import { Thread, useChatContext, useChannelActionContext, useMessageComposer } from "stream-chat-react";
import { useStartDirectChat } from "@/features/chat/hooks/useStartDirectChat";
import { motion, AnimatePresence } from "framer-motion";
import { UserAvatar } from "@/features/chat/components/UserAvatar";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useActiveCall } from "@/features/call/context/ActiveCallContext";
import { formatLastSeen } from "@/shared/utils/formatters";
import { useAuth } from "@/features/auth/context/AuthContext";
import { chatTemplateService } from "@/features/other-setup/services/chatTemplateService";
import { getAttachmentViewingUrl, proxyDownloadAsFile } from "@/shared/services/uploadService";
import type { ChatTemplate, TemplateAttachment } from "@/features/other-setup/types/chatTemplate";

// ─── Types ───────────────────────────────────────────────────────────────────

type SidebarTab = "members" | "files" | "pinned" | "templates";

interface AttachmentItem {
  id: string;
  messageId: string;
  senderId: string;
  kind: "image" | "file" | "video" | "audio";
  title: string;
  url: string;
  thumbUrl?: string;
  fileSize?: number;
  mimeType?: string;
  sentAt: Date;
  senderName: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatFileSize = (bytes?: number): string => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const groupByMonth = (
  items: AttachmentItem[],
): { label: string; items: AttachmentItem[] }[] => {
  const buckets = new Map<string, AttachmentItem[]>();
  for (const item of items) {
    const key = `${MONTH_NAMES[item.sentAt.getMonth()]} ${item.sentAt.getFullYear()}`;
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key)!.push(item);
  }
  return Array.from(buckets.entries()).map(([label, items]) => ({
    label,
    items,
  }));
};

// ─── Pinned Message Row ───────────────────────────────────────────────────────

const PinnedMessageRow = ({
  message,
  onUnpin,
  onJump,
}: {
  message: any;
  onUnpin: () => void;
  onJump: () => void;
}) => {
  const senderName = message.user?.name || message.user?.id || "Unknown";
  const hasAttachment = !message.text && (message.attachments?.length ?? 0) > 0;
  const preview = message.text
    ? message.text.length > 100
      ? message.text.slice(0, 100) + "…"
      : message.text
    : hasAttachment
      ? "📎 Attachment"
      : "Message";
  const pinnedAt = message.pinned_at ? new Date(message.pinned_at) : null;

  return (
    <div
      className="px-4 py-3 border-b border-slate-100 hover:bg-slate-50 group transition-colors cursor-pointer"
      onClick={onJump}
      title="Jump to message"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-slate-700 truncate">
            {senderName}
          </p>
          <p className="text-xs text-slate-500 mt-0.5 leading-snug line-clamp-2">
            {preview}
          </p>
          {pinnedAt && (
            <p className="text-[10px] text-slate-400 mt-1">
              {pinnedAt.toLocaleDateString([], {
                month: "short",
                day: "numeric",
              })}
            </p>
          )}
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onUnpin();
          }}
          className="flex-shrink-0 p-1 text-slate-300 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all rounded"
          title="Unpin message"
        >
          <svg
            className="w-3.5 h-3.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

// ─── Pinned Messages Panel ────────────────────────────────────────────────────

const PinnedMessagesPanel = () => {
  const { channel, client } = useChatContext();
  const { jumpToMessage } = useChannelActionContext("PinnedMessagesPanel");
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!channel) return;
    const bump = () => setTick((t) => t + 1);
    const s1 = channel.on("message.updated", bump);
    return () => {
      s1.unsubscribe();
    };
  }, [channel]);

  const pinnedMessages = useMemo(() => {
    void tick;
    return [...(channel?.state?.pinnedMessages ?? [])].reverse();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel?.state?.pinnedMessages, tick]);

  const handleUnpin = async (msg: any) => {
    try {
      await client.unpinMessage(msg);
    } catch (err) {
      console.error("[PinnedMessagesPanel] Failed to unpin:", err);
    }
  };

  if (pinnedMessages.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center flex-1 gap-2 px-4 text-slate-400">
        <svg
          className="w-8 h-8 opacity-40"
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path d="M16 3a1 1 0 0 1 .707 1.707L15.414 6l1.293 1.293a1 1 0 0 1-1.414 1.414L14 7.414l-4 4V13a1 1 0 0 1-.293.707l-5 5A1 1 0 0 1 3 17.586l5-5A1 1 0 0 1 9 12v-1.586l4-4-1.293-1.293A1 1 0 0 1 13.086 3.5L14.5 4.914 15.293 4.12A1 1 0 0 1 16 3Z" />
        </svg>
        <p className="text-sm text-center">No pinned messages</p>
        <p className="text-xs text-center opacity-70">
          Right-click any message and choose "Pin / Unpin"
        </p>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      {pinnedMessages.map((msg: any) => (
        <PinnedMessageRow
          key={msg.id}
          message={msg}
          onUnpin={() => handleUnpin(msg)}
          onJump={() => jumpToMessage(msg.id)}
        />
      ))}
    </div>
  );
};

// ─── Root Component ───────────────────────────────────────────────────────────

interface RightSidebarProps {
  threadOpen: boolean;
  onCloseThread: () => void;
  isGroup: boolean;
}

export const RightSidebar = ({
  threadOpen,
  onCloseThread,
  isGroup,
}: RightSidebarProps) => {
  const { channel } = useChatContext();
  const { userData } = useAuth();
  const isOfficer = ["OFFICER", "ADMIN"].includes((userData?.userType ?? "").toUpperCase());

  const [activeTab, setActiveTab] = useState<SidebarTab>(
    isGroup ? "members" : "files",
  );

  // Reset tab when conversation changes
  useEffect(() => {
    setActiveTab(isGroup ? "members" : "files");
  }, [channel?.id, isGroup]);

  // Auto-switch to pinned tab when pinned messages exist
  // (intentionally not auto-switching — user controls tab)

  return (
    <div className="hidden md:flex h-full">
      <AnimatePresence mode="wait">
        <motion.div
          key={threadOpen ? "thread" : "content"}
          initial={{ width: 0, opacity: 0 }}
          animate={{ width: 280, opacity: 1 }}
          exit={{ width: 0, opacity: 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className="border-l border-slate-200 bg-white overflow-hidden flex flex-col h-full"
        >
          {threadOpen ? (
            <ThreadPanel onClose={onCloseThread} />
          ) : (
            <>
              {/* Tab Bar — always visible */}
              <div className="flex border-b border-slate-200 flex-shrink-0">
                {isGroup && (
                  <TabButton
                    label="Members"
                    active={activeTab === "members"}
                    onClick={() => setActiveTab("members")}
                  />
                )}
                <TabButton
                  label="Files"
                  active={activeTab === "files"}
                  onClick={() => setActiveTab("files")}
                />
                <TabButton
                  label="Pinned"
                  active={activeTab === "pinned"}
                  onClick={() => setActiveTab("pinned")}
                />
                {isOfficer && (
                  <TabButton
                    label="Templates"
                    active={activeTab === "templates"}
                    onClick={() => setActiveTab("templates")}
                  />
                )}
              </div>

              {activeTab === "members" && isGroup ? (
                <MembersPanel />
              ) : activeTab === "pinned" ? (
                <PinnedMessagesPanel />
              ) : activeTab === "templates" && isOfficer ? (
                <TemplatesPanel />
              ) : (
                <FilesPanel />
              )}
            </>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

// ─── Tab Button ───────────────────────────────────────────────────────────────

const TabButton = ({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) => (
  <button
    onClick={onClick}
    className={`flex-1 py-2.5 text-xs font-semibold transition-colors border-b-2 ${
      active
        ? "text-blue-600 border-blue-600"
        : "text-slate-500 border-transparent hover:text-slate-700"
    }`}
  >
    {label}
  </button>
);

// ─── Thread Panel ─────────────────────────────────────────────────────────────

const ThreadPanel = ({ onClose }: { onClose: () => void }) => (
  <div className="flex flex-col h-full">
    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
      <h3 className="text-sm font-semibold text-slate-900">Thread</h3>
      <button
        onClick={onClose}
        className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"
      >
        <svg
          className="w-4 h-4 text-slate-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>
    <div className="flex-1 overflow-hidden">
      <Thread />
    </div>
  </div>
);

// ─── Members Panel ────────────────────────────────────────────────────────────

const MembersPanel = () => {
  const { channel, client } = useChatContext();
  const { call } = useActiveCall();
  const startDirectChat = useStartDirectChat();
  const members = Object.values(channel?.state?.members || {});

  const [activeCallId, setActiveCallId] = useState<string | undefined>(
    (channel?.data as any)?.activeCallId,
  );

  useEffect(() => {
    if (!channel) return;
    setActiveCallId((channel.data as any)?.activeCallId);
    const subscription = channel.on("channel.updated", (event) => {
      setActiveCallId((event.channel as any)?.activeCallId);
    });
    return () => subscription.unsubscribe();
  }, [channel]);

  const [participantIds, setParticipantIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!call) {
      setParticipantIds(new Set());
      return;
    }
    const update = () =>
      setParticipantIds(
        new Set((call.state.participants as any[]).map((p) => p.userId)),
      );
    update();
    const sub = (call.state as any).participants$?.subscribe?.(() => update());
    return () => sub?.unsubscribe?.();
  }, [call]);

  const currentUserInCall = !!activeCallId && call?.id === activeCallId;

  const handleAskToJoin = useCallback(
    async (_memberId: string, _memberName: string) => {
      // Ring the active call — the SDK sends a push notification to all
      // call members who have not yet joined (same ring mechanic used by
      // startCall / startGroupCall in ActiveCallContext).
      if (!call) return;
      try {
        await call.ring();
      } catch (err) {
        console.error("[RightSidebar] Failed to ring member:", err);
      }
    },
    [call],
  );

  const onlineMembers = members.filter((m) => m.user?.online);
  const offlineMembers = members.filter((m) => !m.user?.online);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="flex-1 overflow-y-auto">
        {onlineMembers.length > 0 && (
          <>
            <p className="px-4 pt-3 pb-1 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
              Online &mdash; {onlineMembers.length}
            </p>
            {onlineMembers.map((m) => {
              const showAskToJoin =
                currentUserInCall &&
                m.user?.id !== client?.userID &&
                !participantIds.has(m.user?.id || "");
              const isMe = m.user?.id === client?.userID;
              return (
                <MemberRow
                  key={m.user!.id}
                  user={m.user!}
                  online
                  showAskToJoin={showAskToJoin}
                  onAskToJoin={() =>
                    handleAskToJoin(m.user!.id, m.user!.name || m.user!.id)
                  }
                  onMemberClick={isMe ? undefined : () => startDirectChat(m.user!.id)}
                />
              );
            })}
          </>
        )}
        {offlineMembers.length > 0 && (
          <>
            <p className="px-4 pt-3 pb-1 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
              Offline &mdash; {offlineMembers.length}
            </p>
            {offlineMembers.map((m) => {
              const isMe = m.user?.id === client?.userID;
              return (
                <MemberRow
                  key={m.user!.id}
                  user={m.user!}
                  online={false}
                  onMemberClick={isMe ? undefined : () => startDirectChat(m.user!.id)}
                />
              );
            })}
          </>
        )}
        {members.length === 0 && (
          <p className="px-4 py-8 text-sm text-slate-400 text-center">
            No members
          </p>
        )}
      </div>
    </div>
  );
};

// ─── Member Row ───────────────────────────────────────────────────────────────

const MemberRow = ({
  user,
  online,
  showAskToJoin,
  onAskToJoin,
  onMemberClick,
}: {
  user: any;
  online: boolean;
  showAskToJoin?: boolean;
  onAskToJoin?: () => void;
  onMemberClick?: () => void;
}) => {
  const isOfficer =
    ((user as any)?.user_type || "").toLowerCase() === "officer";
  const company = (user as any)?.company_name || (user as any)?.company || "";
  const lastSeen = !online ? formatLastSeen((user as any)?.last_active) : "";

  return (
    <div className="flex items-center gap-3 px-4 py-2 hover:bg-slate-50 transition-colors">
      <div className="relative flex-shrink-0">
        <UserAvatar
          name={user.name}
          id={user.id}
          image={user.image}
          size={32}
        />
        <span
          className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2 border-white ${
            online ? "bg-green-500" : "bg-slate-300"
          }`}
        />
      </div>
      <div className="min-w-0 flex-1">
        <p
          className={`text-sm font-medium text-slate-800 truncate ${onMemberClick ? "cursor-pointer hover:text-blue-600 hover:underline" : ""}`}
          onClick={onMemberClick}
        >
          {user.name || user.id}
        </p>
        {!isOfficer && company && (
          <p className="text-[11px] text-slate-400 truncate">{company}</p>
        )}
        {lastSeen && (
          <p className="text-[10px] text-slate-300 truncate">{lastSeen}</p>
        )}
      </div>
      {showAskToJoin && onAskToJoin && (
        <button
          onClick={onAskToJoin}
          title="Ask to join call"
          className="flex-shrink-0 text-[11px] font-medium text-green-700 bg-green-50 hover:bg-green-100 border border-green-200 rounded-full px-2.5 py-1 transition-colors"
        >
          Ask
        </button>
      )}
    </div>
  );
};

// ─── Templates Panel ─────────────────────────────────────────────────────────

const isImageAttachment = (a: TemplateAttachment) =>
  a.file_type?.startsWith("image/") ||
  /\.(jpg|jpeg|png|gif|webp)$/i.test(a.display_name ?? "");

const TemplatesPanel = () => {
  const [templates, setTemplates] = useState<ChatTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});
  const [injectingIds, setInjectingIds] = useState<Set<string>>(new Set());
  const messageComposer = useMessageComposer();

  useEffect(() => {
    chatTemplateService
      .getActive()
      .then(setTemplates)
      .catch(() => setTemplates([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = search.trim()
    ? templates.filter(
        (t) =>
          t.title.toLowerCase().includes(search.toLowerCase()) ||
          t.body.toLowerCase().includes(search.toLowerCase()) ||
          (t.category ?? "").toLowerCase().includes(search.toLowerCase()),
      )
    : templates;

  // Group by category; uncategorised goes last under empty string key
  const grouped = useMemo(() => {
    const map = new Map<string, ChatTemplate[]>();
    for (const t of filtered) {
      const key = t.category ?? "";
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(t);
    }
    // Sort: named categories first (alphabetical), then uncategorised
    return [...map.entries()].sort(([a], [b]) => {
      if (!a && b) return 1;
      if (a && !b) return -1;
      return a.localeCompare(b);
    });
  }, [filtered]);

  // Pre-load viewing URLs for image attachments so previews render instantly
  useEffect(() => {
    const imageAttachments = templates.flatMap((t) =>
      (t.attachments ?? []).filter(isImageAttachment),
    );
    for (const a of imageAttachments) {
      if (previewUrls[a.attachment_id]) continue;
      getAttachmentViewingUrl(a.attachment_id).then((url) =>
        setPreviewUrls((prev) => ({ ...prev, [a.attachment_id]: url })),
      ).catch(() => {});
    }
  }, [templates]); // eslint-disable-line react-hooks/exhaustive-deps

  const injectText = (body: string) => {
    const textarea = document.querySelector(
      ".str-chat__textarea__textarea",
    ) as HTMLTextAreaElement | null;
    if (!textarea) return;
    const setter = Object.getOwnPropertyDescriptor(
      window.HTMLTextAreaElement.prototype,
      "value",
    )?.set;
    setter?.call(textarea, body);
    textarea.dispatchEvent(new Event("input", { bubbles: true }));
    textarea.focus();
  };

  const handleSelect = async (template: ChatTemplate) => {
    injectText(template.body);

    const attachments = template.attachments ?? [];
    if (!attachments.length) return;

    // Download each attachment from our backend proxy (same-origin, no CORS)
    // then upload to Stream's CDN via attachmentManager so files appear in the
    // native compose area and are stored on Stream's own storage.
    setInjectingIds(new Set(attachments.map((a) => a.attachment_id)));
    try {
      const files = await Promise.all(
        attachments.map((a) =>
          proxyDownloadAsFile(a.attachment_id, a.display_name, a.file_type),
        ),
      );
      messageComposer.attachmentManager.uploadFiles(files);
    } catch {
      // Text was still injected; silently skip file injection on error
    } finally {
      setInjectingIds(new Set());
    }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header + search */}
      <div className="px-4 py-3 border-b border-slate-200 flex-shrink-0 space-y-2">
        <h3 className="text-sm font-semibold text-slate-900">Templates</h3>
        <div className="relative">
          <svg
            className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search templates…"
            className="w-full pl-8 pr-3 py-1.5 text-xs border border-slate-200 rounded-lg outline-none focus:border-indigo-400 transition-colors"
          />
        </div>
      </div>

      {/* Body */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center text-xs text-slate-400">
          Loading…
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-2 px-4 text-slate-400">
          <svg className="w-7 h-7 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
          <p className="text-xs text-center">
            {search ? "No templates match your search." : "No templates available."}
          </p>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto pb-4">
          {grouped.map(([category, items]) => (
            <div key={category || "__none__"}>
              {category && (
                <p className="px-4 pt-3 pb-1 text-[11px] font-semibold text-slate-400 uppercase tracking-wider sticky top-0 bg-white z-10">
                  {category}
                </p>
              )}
              {!category && grouped.length > 1 && (
                <p className="px-4 pt-3 pb-1 text-[11px] font-semibold text-slate-400 uppercase tracking-wider sticky top-0 bg-white z-10">
                  Other
                </p>
              )}
              {items.map((template) => {
                const attachments = template.attachments ?? [];
                const images = attachments.filter(isImageAttachment);
                const files = attachments.filter((a) => !isImageAttachment(a));
                const isInjecting = attachments.some((a) => injectingIds.has(a.attachment_id));
                return (
                  <button
                    key={template.id}
                    onClick={() => handleSelect(template)}
                    disabled={isInjecting}
                    className="w-full text-left px-4 py-2.5 hover:bg-indigo-50 active:bg-indigo-100 transition-colors group border-b border-slate-50 disabled:opacity-60"
                    title="Click to use this template"
                  >
                    <p className="text-xs font-semibold text-slate-700 group-hover:text-indigo-700 transition-colors">
                      {template.title}
                    </p>
                    <p className="text-[11px] text-slate-400 mt-0.5 line-clamp-2 leading-snug">
                      {template.body}
                    </p>

                    {/* Image previews */}
                    {images.length > 0 && (
                      <div className="mt-1.5 flex gap-1 flex-wrap">
                        {images.map((a) =>
                          previewUrls[a.attachment_id] ? (
                            <img
                              key={a.attachment_id}
                              src={previewUrls[a.attachment_id]}
                              alt={a.display_name}
                              className="w-12 h-12 object-cover rounded border border-slate-200"
                            />
                          ) : (
                            <div
                              key={a.attachment_id}
                              className="w-12 h-12 rounded border border-slate-200 bg-slate-100 flex items-center justify-center text-slate-400 text-lg"
                            >
                              🖼
                            </div>
                          ),
                        )}
                      </div>
                    )}

                    {/* File chips */}
                    {files.length > 0 && (
                      <div className="mt-1.5 flex gap-1 flex-wrap">
                        {files.map((a) => (
                          <span
                            key={a.attachment_id}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] bg-slate-100 text-slate-600 rounded border border-slate-200 max-w-[120px]"
                          >
                            <svg className="w-3 h-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                            </svg>
                            <span className="truncate">{a.display_name}</span>
                          </span>
                        ))}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Files Panel ──────────────────────────────────────────────────────────────

const FilesPanel = () => {
  const { channel, client } = useChatContext();

  // Bump this to force useMemo to re-run when messages change
  const [tick, setTick] = useState(0);

  // Listen for message deletions and edits → refresh the list
  useEffect(() => {
    if (!channel) return;
    const bump = () => setTick((t) => t + 1);
    const s1 = channel.on("message.deleted", bump);
    const s2 = channel.on("message.updated", bump);
    return () => {
      s1.unsubscribe();
      s2.unsubscribe();
    };
  }, [channel]);

  // Two-step deletion: first click arms, second click fires
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  // Auto-disarm confirmation after 3 s of inactivity
  useEffect(() => {
    if (!confirmingId) return;
    const timer = setTimeout(() => setConfirmingId(null), 3000);
    return () => clearTimeout(timer);
  }, [confirmingId]);

  const handleDeleteItem = useCallback(
    async (item: AttachmentItem) => {
      if (confirmingId !== item.id) {
        // First click — arm
        setConfirmingId(item.id);
        return;
      }
      // Second click — execute
      setConfirmingId(null);
      try {
        await client.deleteMessage(item.messageId);
        // The message.deleted listener above will bump tick and re-derive attachments
      } catch (err) {
        console.error("[FilesPanel] Failed to delete message:", err);
      }
    },
    [client, confirmingId],
  );

  // Dismiss armed confirmation when clicking elsewhere
  const handlePanelClick = useCallback(
    (e: React.MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest("[data-delete-btn]")) {
        setConfirmingId(null);
      }
    },
    [],
  );

  // Extract attachments from the in-memory message list.
  // `tick` is a dependency so this re-runs after deletions/updates.
  const attachments = useMemo<AttachmentItem[]>(() => {
    void tick; // explicit dependency
    const msgs: any[] = channel?.state?.messages || [];
    const items: AttachmentItem[] = [];

    for (const msg of msgs) {
      // Skip deleted, system, or call-log messages
      if (msg.type === "deleted" || msg.deleted_at) continue;
      if (msg.type === "system" || (msg as any).call_log) continue;
      if (!msg.attachments?.length) continue;

      for (const att of msg.attachments as any[]) {
        // Skip giphy, voicenote, and URL previews
        if (att.type === "giphy" || att.type === "voicenote" || att.og_scrape_url)
          continue;

        const url = att.image_url || att.asset_url || att.thumb_url || "";
        if (!url) continue;

        let kind: AttachmentItem["kind"] = "file";
        if (att.type === "image" || att.mime_type?.startsWith("image/")) {
          kind = "image";
        } else if (att.type === "video" || att.mime_type?.startsWith("video/")) {
          kind = "video";
        } else if (att.type === "audio" || att.mime_type?.startsWith("audio/")) {
          kind = "audio";
        }

        items.push({
          id: `${msg.id}__${url}`,
          messageId: msg.id,
          senderId: msg.user?.id || "",
          kind,
          title: att.title || att.fallback || "Untitled",
          url,
          thumbUrl: att.thumb_url || (kind === "image" ? url : undefined),
          fileSize: att.file_size,
          mimeType: att.mime_type,
          sentAt: msg.created_at ? new Date(msg.created_at) : new Date(),
          senderName: msg.user?.name || msg.user?.id || "Unknown",
        });
      }
    }

    return items.sort((a, b) => b.sentAt.getTime() - a.sentAt.getTime());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel?.state?.messages, tick]);

  const groups = useMemo(() => groupByMonth(attachments), [attachments]);

  const renderGroup = (group: { label: string; items: AttachmentItem[] }) => {
    const images = group.items.filter((i) => i.kind === "image");
    const files = group.items.filter((i) => i.kind !== "image");

    return (
      <div key={group.label}>
        {/* Month / Year sticky header */}
        <p className="px-4 pt-4 pb-1.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wider sticky top-0 bg-white z-10">
          {group.label}
        </p>

        {/* Image grid */}
        {images.length > 0 && (
          <div className="px-3 grid grid-cols-3 gap-1 mb-1">
            {images.map((item) => {
              const isOwner = item.senderId === client?.userID;
              const isConfirming = confirmingId === item.id;
              return (
                <div key={item.id} className="relative group aspect-square">
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={item.title}
                    className="block w-full h-full rounded-md overflow-hidden bg-slate-100 hover:opacity-90 transition-opacity"
                  >
                    <img
                      src={item.thumbUrl || item.url}
                      alt={item.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).style.display =
                          "none";
                      }}
                    />
                  </a>

                  {/* Delete overlay — own messages only */}
                  {isOwner && (
                    <>
                      {isConfirming ? (
                        // Confirming state: full red overlay
                        <button
                          data-delete-btn
                          onClick={() => handleDeleteItem(item)}
                          className="absolute inset-0 rounded-md bg-red-500/80 flex flex-col items-center justify-center gap-0.5 cursor-pointer"
                          title="Tap again to delete"
                        >
                          <svg
                            className="w-4 h-4 text-white"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                          <span className="text-[10px] text-white font-semibold">
                            Delete?
                          </span>
                        </button>
                      ) : (
                        // Normal state: small trash icon top-right
                        <button
                          data-delete-btn
                          onClick={(e) => {
                            e.preventDefault();
                            handleDeleteItem(item);
                          }}
                          className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-red-500 transition-all"
                          title="Delete file"
                        >
                          <svg
                            className="w-2.5 h-2.5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* File list */}
        {files.map((item) => {
          const isOwner = item.senderId === client?.userID;
          const isConfirming = confirmingId === item.id;
          return (
            <div
              key={item.id}
              className={`flex items-center gap-3 px-4 py-2.5 group transition-colors ${
                isConfirming ? "bg-red-50" : "hover:bg-slate-50"
              }`}
            >
              <a
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 flex-1 min-w-0"
              >
                <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center group-hover:bg-slate-200 transition-colors">
                  <FileTypeIcon mimeType={item.mimeType} kind={item.kind} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-slate-800 truncate">
                    {item.title}
                  </p>
                  <p className="text-[11px] text-slate-400 truncate">
                    {[item.senderName, formatFileSize(item.fileSize)]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                </div>
                <svg
                  className="w-3.5 h-3.5 text-slate-300 group-hover:text-slate-500 flex-shrink-0 transition-colors"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
              </a>

              {/* Delete button — own messages only */}
              {isOwner && (
                <button
                  data-delete-btn
                  onClick={() => handleDeleteItem(item)}
                  title={isConfirming ? "Tap again to confirm" : "Delete file"}
                  className={`flex-shrink-0 flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-medium transition-all ${
                    isConfirming
                      ? "bg-red-500 text-white"
                      : "bg-transparent text-slate-300 opacity-0 group-hover:opacity-100 hover:text-red-500"
                  }`}
                >
                  {isConfirming ? (
                    "Delete?"
                  ) : (
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  )}
                </button>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  if (attachments.length === 0) {
    return (
      <div className="flex flex-col h-full">
        <div className="px-4 py-3 border-b border-slate-200 flex-shrink-0">
          <h3 className="text-sm font-semibold text-slate-900">Files</h3>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center gap-2 px-4 text-center">
          <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
            <svg
              className="w-5 h-5 text-slate-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
              />
            </svg>
          </div>
          <p className="text-sm font-medium text-slate-600">No files yet</p>
          <p className="text-xs text-slate-400">
            Shared images and files will appear here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden" onClick={handlePanelClick}>
      <div className="px-4 py-3 border-b border-slate-200 flex-shrink-0">
        <h3 className="text-sm font-semibold text-slate-900">
          Files{" "}
          <span className="text-slate-400 font-normal">
            ({attachments.length})
          </span>
        </h3>
      </div>
      <div className="flex-1 overflow-y-auto pb-4">
        {groups.map(renderGroup)}
      </div>
    </div>
  );
};

// ─── File Type Icon ───────────────────────────────────────────────────────────

const FileTypeIcon = ({
  mimeType,
  kind,
}: {
  mimeType?: string;
  kind: AttachmentItem["kind"];
}) => {
  if (kind === "video") {
    return (
      <svg
        className="w-4 h-4 text-purple-500"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 10l4.553-2.069A1 1 0 0121 8.882v6.236a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
        />
      </svg>
    );
  }
  if (kind === "audio") {
    return (
      <svg
        className="w-4 h-4 text-pink-500"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"
        />
      </svg>
    );
  }
  if (mimeType?.includes("pdf")) {
    return (
      <svg
        className="w-4 h-4 text-red-500"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
        />
      </svg>
    );
  }
  if (mimeType?.includes("word") || mimeType?.includes("document")) {
    return (
      <svg
        className="w-4 h-4 text-blue-500"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
    );
  }
  if (
    mimeType?.includes("sheet") ||
    mimeType?.includes("excel") ||
    mimeType?.includes("csv")
  ) {
    return (
      <svg
        className="w-4 h-4 text-green-500"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3 10h18M3 14h18M10 3v18M3 6a1 1 0 011-1h16a1 1 0 011 1v12a1 1 0 01-1 1H4a1 1 0 01-1-1V6z"
        />
      </svg>
    );
  }
  return (
    <svg
      className="w-4 h-4 text-slate-500"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
      />
    </svg>
  );
};
